import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useBoolean, useDebounce, useSetState } from 'react-use'

import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { isEmail, OrgCardAdapter } from '@makyoapp/makyo-common'

import { useDeleteAccountMutation, useRestoreAccountMutation } from '@/resolvers/login/mutation'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import useCountDown from '@/hooks/useCountDown'
import { useInvite } from '@/hooks/useInvite'
import { DEFAULT_DEBOUNCE_MS, LOGIN_ROUTE, ORG_APP_ROUTE, ORG_ROUTE } from '@/shared/constants'

function printError(err: ErrorResponse | string) {
  let msg
  if (typeof err === 'string') {
    msg = err
  } else {
    msg = err.message
  }

  if (msg?.toLowerCase()?.includes('unauthorized')) {
    return
  } else {
    toast.dismiss('for-all')
    toast.error(msg, {
      containerId: 'for-all',
    })
  }
}

/**
 * Login page state
 */
interface LoginState {
  email: string
  code: string
  twoFaCode: string
  step: number
  isInitial: boolean
  errorMsg: Error | null
}

/**
 * default initial state
 */
const initialState: LoginState = {
  email: '',
  code: '',
  twoFaCode: '',
  step: 1,
  isInitial: true,
  errorMsg: null,
}

/**
 * Function to redirect the user after login.
 *
 * @param {string} userId - The user's ID.
 * @param {NavigateFunction} navigate - The function to navigate to different pages.
 * @param {OrgCardAdapter[]} orgs - An array of the user's organizations.
 * @param {Location} location - The current location object, which contains information about the current URL.
 */
function loginRedirect(userId: string, navigate: NavigateFunction, orgs: OrgCardAdapter[], location: Location) {
  const locationState = location.state as { from: Location } | undefined
  const searchParams = locationState?.from.search
  const spStr = searchParams !== undefined ? searchParams : ''

  if (orgs.length > 0) {
    const defaultOrgs = orgs.find((org) => org.isDefault && org.ownerId === userId)

    if (defaultOrgs) {
      navigate(`${ORG_ROUTE}/${defaultOrgs.id}/${ORG_APP_ROUTE}${spStr}`, {
        replace: true,
        state: {
          from: location,
        },
      })
    } else {
      navigate(`/${searchParams}`, {
        replace: true,
        state: {
          from: location,
        },
      })
    }
  } else {
    navigate(`/${searchParams}`, {
      replace: true,
      state: {
        from: location,
      },
    })
  }
}

/**
 * Custom hook to handle all logic related to the login process.
 *
 * @returns {
 *  email: string,
 *  code: string,
 *  twoFaCode: string,
 *  buttonDisabled: boolean,
 *  state: object,
 *  timeLeft: number,
 *  showRestore: string,
 *  isRestoreLoading: boolean,
 *  isLoginPending: boolean,
 *  isResend: boolean,
 *  isInvite: boolean,
 *  data: object,
 *  handleKeyUp: function,
 *  onSubmit: function,
 *  handleEmailInput: function,
 *  handleCodeInput: function,
 *  handleResendCode: function,
 *  handleInputTFA: function,
 *  handleDeleteAccount: function,
 *  handleLogout: function,
 *  handleRestoreAccount: function
 * }
 *
 * The returned object includes state variables related to the login process,
 * as well as several methods for handling form submission, input changes,
 * account deletion, logout, and account restoration.
 */
const useLogin = () => {
  const { t } = useTranslation()
  const { userData, logout, isLoginPending, firstStep, secondStep, thirdStep } = useAuth()

  const navigate = useNavigate()
  const location = useLocation()

  const [state, setState] = useSetState(initialState)
  const [showRestore, setShowRestore] = useState('')
  const [isRestoreLoading, setIsRestoreLoading] = useBoolean(false)

  const { action, data } = useInvite()
  const isInvite = action !== null

  const [isResend, setIsResend] = useBoolean(false)
  const { orgs, setOrgs, updateOrgs, orgsPerms, setOrgsPerms, updateOrgsPerms } = useOrgsContext()

  //Mutations
  const { mutateAsync: deleteAccountMutation } = useDeleteAccountMutation()
  const { mutateAsync: restoreAccountMutation } = useRestoreAccountMutation()

  /**
   * Effect hook that is triggered when the 'setOrgs', 'setOrgsPerms', or 'userData' variables change.
   * If the user is not logged in (i.e., 'userData' is falsey), it will clear the 'orgs' and 'orgsPerms' state variables.
   */
  useEffect(() => {
    if (orgs.length > 0 && !userData) {
      setOrgs([]) // Clear 'orgs' if user is not logged in
    }
    if (Object.keys(orgsPerms).length > 0 && !userData) {
      setOrgsPerms({}) // Clear 'orgsPerms' if user is not logged in
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOrgs, setOrgsPerms, userData])

  /**
   * handle form submit
   */
  /**
   * This async function handles form submission.
   * It controls login process which is divided into three steps.
   * Each step handles different part of login process:
   * 1. Accepts email and initiates the login process.
   * 2. Accepts code and validates it.
   * 3. If two-factor authentication is enabled, it validates the TFA code.
   */
  const onSubmit = async () => {
    // Get email, code and current step from state
    const { email, code, step } = state

    if (step === 1) {
      // First step of login
      try {
        await firstStep(email) // Initiate login process
        setButtonDisabled(true)
        // If first step is successful, move to second step
        setState({
          step: 2,
          isInitial: true,
        })
      } catch (e: unknown) {
        setButtonDisabled(true)
        // Set error message and stay on first step
        setState({
          errorMsg: e as Error,
          step: 1,
          isInitial: false,
        })
      }
    } else if (step === 2 && !isNaN(Number(code))) {
      // Second step of login
      try {
        const { result: secondResult, done } = await secondStep(code) // Validate code
        if (typeof secondResult === 'boolean') {
          // If second step is successful and two-factor authentication is enabled, move to third step
          setState({
            step: 3,
            isInitial: true,
          })
        } else if (typeof secondResult === 'object') {
          if (secondResult.isDeleted) {
            setShowRestore(secondResult.id)
            done?.()
          } else {
            const newOrgs = await updateOrgs().then((orgs) => {
              updateOrgsPerms(orgs)
              return orgs
            })
            done?.()
            loginRedirect(secondResult.id, navigate, newOrgs, location)
          }
        }
      } catch (e: unknown) {
        // Set error message and stay on second step
        setState({
          errorMsg: e as Error,
        })
      }
    } else if (step === 3 && !isNaN(Number(twoFaCode))) {
      // Third step of login
      try {
        const { result: thirdResult, done } = await thirdStep(twoFaCode) // Validate TFA code
        if (thirdResult?.isDeleted) {
          // If account is deleted, show account restore option
          setShowRestore(thirdResult.id)
          done?.()
        } else {
          // Update organizations and permissions
          const newOrgs = await updateOrgs().then(() => {
            updateOrgsPerms(orgs)
            return orgs
          })
          done?.()
          // If TFA validation is successful, redirect to dashboard
          loginRedirect(thirdResult?.id || '', navigate, newOrgs, location)
        }
      } catch (e: unknown) {
        // Set error message and stay on third step
        setState({
          errorMsg: e as Error,
        })
      }
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !state.errorMsg) {
      onSubmit().catch(console.error)
    }
  }

  //const [emailUpdate, setEmailUpdate] = useState<boolean>(true)
  const { email, code, twoFaCode } = state
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const isInitial = state.isInitial

  const [debounceEmail, setDebounceEmail] = React.useState('')

  // debounce error with timeout
  const [, cancel] = useDebounce(
    () => {
      setDebounceEmail(email)
    },
    DEFAULT_DEBOUNCE_MS,
    [email]
  )

  useEffect(() => {
    // This effect runs when the 'isInitial' or 'debounceEmail' value changes.
    // It validates the email syntax as soon as the user stops typing (debouncing effect).

    // 'isInitial' is false after the user has interacted with the form.
    if (!isInitial && debounceEmail) {
      // Check if entered email is valid
      if (!isEmail(debounceEmail)) {
        // If the email is invalid, disable the submit button and show an error message
        setButtonDisabled(true)
        setState({
          errorMsg: new Error(t('Invalid email address')),
        })
      } else {
        // If the email is valid, enable the submit button and clear any error message
        setButtonDisabled(false)
        setState({
          errorMsg: null,
        })
      }
    }
  }, [isInitial, debounceEmail, setState, setButtonDisabled, t]) // Depend on 'isInitial', 'debounceEmail', 'setState', 'setButtonDisabled', and 't' values

  const [timeLeft, { start }] = useCountDown(15000, 1000)

  // This effect triggers the start function when the current step is two
  useEffect(() => {
    if (state.step === 2) {
      start()
    }
  }, [start, state.step]) // Depend on 'start' function and 'state.step' value

  // This function is called when the email input field value changes
  const handleEmailInput = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value
    // Validate the email
    if (isEmail(val)) {
      // If the email is valid, enable the submit button
      // and update the state with the new email, and clear any error message
      setButtonDisabled(false)
      setState({
        email: val,
        isInitial: false,
        errorMsg: null,
      })
      // email is correct, cancel debounce here
      cancel()
    } else {
      // If the email is not valid, update the state with the new email
      setState({
        email: val,
        isInitial: false,
      })
    }
  }

  // This function is called when the code input field value changes
  const handleCodeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setState({ isInitial: false })
    const v = e.currentTarget.value.trim()
    const len = v?.length
    const codeNum = Number(v)
    setState({ code: v })

    // Validate the code
    if (!isNaN(codeNum)) {
      if (len !== 6) {
        // If the code is not 6 digits, disable the submit button
        // and show an error message
        setButtonDisabled(true)
        setState({
          errorMsg: new Error(t('OTP must be 6 digits')),
        })
      } else {
        // If the code is valid, enable the submit button and clear any error message
        setButtonDisabled(false)
        setState({
          errorMsg: null,
        })
      }
    } else {
      // If the code is not a number, disable the submit button and show an error message
      setButtonDisabled(true)
      setState({
        errorMsg: new Error(t('Input value is not a number')),
      })
    }
  }

  // Function to handle the resending of the code
  const handleResendCode = async () => {
    let attemp = 0

    // Setting the resend state to true
    setIsResend(true)

    const resendCode = () =>
      firstStep(email).then(() => {
        // Starting the process after the promise is resolved
        start()
        // Setting the resend state to false after the process is started
        setIsResend(false)
      })

    // Calling the first step function with the email
    resendCode().catch(() => {
      if (attemp < 3) {
        attemp++
        setTimeout(resendCode, 500)
      }
    })
  }

  // Function to handle the input for Two Factor Authentication (2FA)
  const handleInputTFA = (e: ChangeEvent<HTMLInputElement>) => {
    // Setting initial state to false
    setState({ isInitial: false })
    // Getting the trimmed value from the input field
    const v = e.currentTarget.value.trim()
    // Getting the length of the input value
    const len = v?.length
    // Converting the value to a number
    const codeNum = Number(v)
    // Setting the state for 2FA code
    setState({ twoFaCode: v })
    // Checking if the value is a number
    if (!isNaN(codeNum)) {
      // Checking if the length of the code is not 6
      if (len !== 6) {
        // Disabling the button and setting the error message in the state
        setButtonDisabled(true)
        setState({
          errorMsg: new Error(t('2FA must be 6 digits')),
        })
      } else {
        // Enabling the button and removing the error message from the state
        setButtonDisabled(false)
        setState({
          errorMsg: null,
        })
      }
    } else {
      // If the value is not a number, disable the button and set the error message in the state
      setButtonDisabled(true)
      setState({
        errorMsg: new Error(t('Input value is not a number')),
      })
    }
  }

  // Function to handle the deletion of the account
  const handleDeleteAccount = () => {
    // Setting the restore loading state to true
    setIsRestoreLoading(true)
    // Calling the delete account mutation
    deleteAccountMutation()
      .then(() => {
        // On success, clear the restore state, logout and redirect to the home page
        setShowRestore('')
        logout()
        window.location.pathname = '/'
      })
      .catch(printError) // On error, print the error
      .finally(() => setIsRestoreLoading(false))
  }

  // Function to handle logout
  const handleLogout = () => {
    // Clear the restore state
    setShowRestore('')
    // Call the logout function
    logout()
    // Redirect to the login page
    window.location.pathname = LOGIN_ROUTE
  }

  // Function to handle account restoration
  const handleRestoreAccount = () => {
    // Set the restore loading state to true
    setIsRestoreLoading(true)
    // Call the restore account mutation
    restoreAccountMutation()
      .then(() => {
        // On success, get the user ID from the restore state
        const userId = showRestore
        // Return a promise to update the organizations
        return updateOrgs()
          .then((orgs) => {
            // After updating the organizations, update the organization permissions
            updateOrgsPerms(orgs)
            // Return the updated organizations
            return orgs
          })
          .then((newOrgs) => {
            // After updating the organization permissions, clear the restore state
            setShowRestore('')
            // If a user ID was found, redirect to the user's dashboard
            if (userId) {
              loginRedirect(userId, navigate, newOrgs, location)
            } else {
              // If no user ID was found, redirect to the home page
              navigate('/')
            }
          })
      })
      // On error, print the error
      .catch(printError)
      // Finally, set the restore loading state to false
      .finally(() => setIsRestoreLoading(false))
  }

  return {
    // State variables
    email,
    code,
    twoFaCode,
    buttonDisabled,
    state,
    timeLeft,
    showRestore,
    isRestoreLoading,
    isLoginPending,
    isResend,
    isInvite,
    data,

    // Methods
    handleKeyUp,
    onSubmit,
    handleEmailInput,
    handleCodeInput,
    handleResendCode,
    handleInputTFA,
    handleDeleteAccount,
    handleLogout,
    handleRestoreAccount,
  }
}

export default useLogin
